/**
 * The thinking-against-an-opponent games: Morpion, Pierre Feuille Ciseaux and
 * Blackjack.
 *
 * Each has an opponent that must be honest. The morpion AI is perfect and says
 * so, the pierre feuille ciseaux opponent never peeks at your choice, and the
 * blackjack dealer follows the published rule rather than whatever keeps the
 * house ahead. An opponent that cheats is the fastest way to make a small game
 * feel worthless, and none of these is hard enough to be worth cheating for.
 */
import { byLevel } from '@arena/games';

import { Lifecycle, h, randomOf, shuffle, tone } from '../kit.js';
import type { GameDef } from '../types.js';

function pill(label: string, value: string): [HTMLElement, HTMLElement] {
  const box = h('div', 'ac-pill');
  const strong = h('strong', '', value);
  box.append(h('span', '', label), strong);
  return [box, strong];
}

// ------------------------------------------------------------------- morpion

export const ticTacToe: GameDef = {
  id: 'tic-tac-toe',
  title: 'Morpion',
  blurb: 'Trois en ligne contre une IA imbattable.',
  category: 'Stratégie',
  difficulty: 'Facile',
  icon: '⭕',
  unit: 'nuls',
  higherIsBetter: true,
  how: 'L’IA joue parfaitement, donc le mieux possible est le match nul. Le score compte tes nuls d’affilee.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const LINES = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8],
      [0, 3, 6], [1, 4, 7], [2, 5, 8],
      [0, 4, 8], [2, 4, 6],
    ];

    /*
     * A perfect opponent has no dial, so difficulty here is a blunder rate and
     * who opens.
     *
     * Easy lets the AI pick at random about a third of the time, which is what
     * finally makes the game winnable: against perfect play a child can only
     * ever draw, forever, and that is not a game they will come back to. Hard
     * keeps perfect play and hands the AI the opening move, which is much
     * harder for a human to answer and is still drawable.
     *
     * Moyen is untouched: a perfect opponent moving second, exactly as before.
     */
    const BLUNDER = byLevel(ctx.level, 0.35, 0, 0);
    const AI_OPENS = ctx.level === 'difficile';

    const [streakBox, streakValue] = pill('Nuls d’affilee', '0');
    const hud = h('div', 'ac-hud');
    hud.append(streakBox);

    let board: Array<'X' | 'O' | ''> = new Array(9).fill('');
    let over = false;
    let streak = 0;

    const winnerOf = (b: typeof board): 'X' | 'O' | null => {
      for (const [a, c, d] of LINES) {
        if (b[a] && b[a] === b[c] && b[a] === b[d]) return b[a] as 'X' | 'O';
      }
      return null;
    };

    /**
     * Minimax, complete. The board is 9 cells, so searching it exhaustively is
     * a few thousand states and finishes instantly; anything heuristic here
     * would be more code AND beatable.
     */
    const minimax = (b: typeof board, forO: boolean): { score: number; move: number } => {
      const win = winnerOf(b);
      if (win === 'O') return { score: 1, move: -1 };
      if (win === 'X') return { score: -1, move: -1 };
      if (b.every((c) => c)) return { score: 0, move: -1 };

      let bestScore = forO ? -2 : 2;
      let bestMove = -1;
      for (let i = 0; i < 9; i++) {
        if (b[i]) continue;
        b[i] = forO ? 'O' : 'X';
        const { score } = minimax(b, !forO);
        b[i] = '';
        if (forO ? score > bestScore : score < bestScore) {
          bestScore = score;
          bestMove = i;
        }
      }
      return { score: bestScore, move: bestMove };
    };

    /** Where the AI plays: perfect, unless this difficulty lets it slip. */
    const aiMove = (): number => {
      const free: number[] = [];
      for (let i = 0; i < 9; i++) if (!board[i]) free.push(i);
      if (!free.length) return -1;
      if (BLUNDER > 0 && Math.random() < BLUNDER) return randomOf(free);
      return minimax(board, true).move;
    };

    const cells: HTMLButtonElement[] = [];
    const board_el = h('div', 'ac-board');
    board_el.style.gridTemplateColumns = 'repeat(3, 1fr)';
    board_el.style.maxWidth = '330px';

    const paint = (): void => {
      cells.forEach((cell, i) => {
        cell.textContent = board[i];
        cell.style.color = board[i] === 'X' ? '#22d3ee' : '#ff2d95';
        cell.disabled = over || board[i] !== '';
      });
      streakValue.textContent = String(streak);
    };

    const finish = (result: 'win' | 'lose' | 'draw'): void => {
      over = true;
      if (result === 'draw') {
        streak++;
        ctx.submit(streak);
        note.textContent = `Match nul. Serie de ${streak}.`;
        tone(620, 110, 'triangle');
      } else if (result === 'lose') {
        streak = 0;
        note.textContent = 'Perdu. La serie repart de zero.';
        tone(160, 260, 'square');
      } else {
        /*
         * A win extends the streak too.
         *
         * It reads as a change to the scoring and is not one: at moyen and
         * above the AI is perfect and cannot be beaten, so "draws in a row" and
         * "games without losing" are the same number, and every record already
         * on those boards still means what it meant. On easy the AI blunders,
         * a win is possible, and counting a win as WORSE than a draw would be
         * absurd.
         */
        streak++;
        ctx.submit(streak);
        note.textContent = `Gagne. Serie de ${streak}.`;
        tone(760, 120, 'triangle');
      }
      paint();
    };

    for (let i = 0; i < 9; i++) {
      const cell = h('button', 'ac-cell') as HTMLButtonElement;
      cell.addEventListener('click', () => {
        if (over || board[i]) return;
        board[i] = 'X';
        tone(500, 45);
        if (winnerOf(board) === 'X') return finish('win');
        if (board.every((c) => c)) return finish('draw');

        const move = aiMove();
        if (move >= 0) board[move] = 'O';
        tone(380, 45);
        if (winnerOf(board) === 'O') return finish('lose');
        if (board.every((c) => c)) return finish('draw');
        paint();
      });
      cells.push(cell);
      board_el.append(cell);
    }

    const note = h('p', 'ac-note', '');
    const HOW = AI_OPENS
      ? 'Tu joues les X. L’IA ouvre et ne fait jamais d’erreur.'
      : BLUNDER > 0
        ? 'Tu joues les X. L’IA se trompe de temps en temps.'
        : 'Tu joues les X. L’IA ne fait jamais d’erreur.';

    const deal = (): void => {
      board = new Array(9).fill('');
      over = false;
      note.textContent = HOW;
      // Hard mode gives the AI the opening move, which is the one advantage a
      // perfect player can still be given.
      if (AI_OPENS) board[minimax(board, true).move] = 'O';
      paint();
    };

    const again = h('button', 'ac-ghost', 'Nouvelle partie');
    again.addEventListener('click', deal);

    deal();
    root.append(hud, board_el, note, again);
    return () => life.stop();
  },
};

// ----------------------------------------------------- pierre feuille ciseaux

export const rockPaperScissors: GameDef = {
  id: 'rock-paper-scissors',
  title: 'Pierre Feuille Ciseaux',
  blurb: 'Contre un adversaire qui lit tes habitudes.',
  category: 'Stratégie',
  difficulty: 'Facile',
  icon: '✌',
  unit: 'victoires',
  higherIsBetter: true,
  how: 'Le score compte tes victoires d’affilee. L’adversaire ne voit pas ton coup, mais il retient tes precedents.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const MOVES = ['pierre', 'feuille', 'ciseaux'] as const;
    const ICONS: Record<string, string> = { pierre: '\u{1FAA8}', feuille: '\u{1F4C4}', ciseaux: '✂' };
    const BEATS: Record<string, string> = { pierre: 'ciseaux', feuille: 'pierre', ciseaux: 'feuille' };

    const [streakBox, streakValue] = pill('Serie', '0');
    const [winBox, winValue] = pill('Victoires', '0');
    const [lossBox, lossValue] = pill('Defaites', '0');
    const hud = h('div', 'ac-hud');
    hud.append(streakBox, winBox, lossBox);

    const showdown = h('p', 'ac-big', 'Choisis');
    showdown.style.margin = '18px 0';

    /*
     * Difficulty is how often the opponent stops reading you and just guesses.
     *
     * That single number is the whole opponent: the rest of the time it plays
     * the counter to whatever you have thrown most. At 72% it is close to a
     * coin, which is roughly fair; at 12% it punishes any habit almost at once,
     * and the only way through is to genuinely mix.
     */
    const COIN_FLIP = byLevel(ctx.level, 0.72, 0.35, 0.12);

    let streak = 0;
    let wins = 0;
    let losses = 0;
    /**
     * A tally of what the player has thrown.
     *
     * The opponent picks the counter to the player's MOST FREQUENT move, which
     * is enough to punish anyone with a habit while staying beatable by anyone
     * who mixes. It is chosen BEFORE seeing this round's move: the history is
     * the only information it has.
     */
    const history: Record<string, number> = { pierre: 0, feuille: 0, ciseaux: 0 };

    const play = (mine: string): void => {
      let theirs: string;
      const total = Object.values(history).reduce((a, b) => a + b, 0);
      if (total < 3 || Math.random() < COIN_FLIP) {
        // Stay random early and often, or it becomes exploitable in reverse.
        theirs = randomOf(MOVES);
      } else {
        const favourite = MOVES.reduce((a, b) => (history[a] >= history[b] ? a : b));
        theirs = MOVES.find((m) => BEATS[m] === favourite)!;
      }
      history[mine]++;

      showdown.textContent = `${ICONS[mine]}  vs  ${ICONS[theirs]}`;
      if (mine === theirs) {
        note.textContent = 'Égalité.';
        tone(420, 70);
        return;
      }
      if (BEATS[mine] === theirs) {
        wins++;
        streak++;
        winValue.textContent = String(wins);
        streakValue.textContent = String(streak);
        ctx.submit(streak);
        note.textContent = `Gagne. ${mine} bat ${theirs}.`;
        tone(720, 90, 'triangle');
      } else {
        losses++;
        streak = 0;
        lossValue.textContent = String(losses);
        streakValue.textContent = '0';
        note.textContent = `Perdu. ${theirs} bat ${mine}.`;
        tone(180, 160, 'square');
      }
    };

    const buttons = h('div');
    buttons.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;justify-content:center';
    for (const m of MOVES) {
      const btn = h('button', 'ac-primary', `${ICONS[m]} ${m}`);
      btn.style.cssText = 'padding:16px 22px;font-size:16px;text-transform:capitalize';
      btn.addEventListener('click', () => play(m));
      buttons.append(btn);
    }

    const note = h('p', 'ac-note', 'Evite les habitudes: il compte ce que tu joues.');
    root.append(hud, showdown, buttons, note);
    return () => life.stop();
  },
};

// ----------------------------------------------------------------- blackjack

export const blackjack: GameDef = {
  id: 'blackjack',
  title: 'Blackjack',
  blurb: 'Approche 21 sans depasser.',
  category: 'Stratégie',
  difficulty: 'Moyen',
  icon: '\u{1F0CF}',
  unit: 'jetons',
  higherIsBetter: true,
  how: 'Tu commences avec 100 jetons et mises 10 par main. Le croupier tire jusqu’a 17. Le score est ton maximum de jetons.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const SUITS = ['♠', '♥', '♦', '♣'];
    const RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

    const [chipBox, chipValue] = pill('Jetons', String(byLevel(ctx.level, 150, 100, 70)));
    const [youBox, youValue] = pill('Toi', '0');
    const [dealerBox, dealerValue] = pill('Croupier', '0');
    const hud = h('div', 'ac-hud');
    hud.append(chipBox, youBox, dealerBox);

    let shoe: string[] = [];
    let mine: string[] = [];
    let theirs: string[] = [];
    /*
     * Difficulty is the size of your bank and one real casino rule.
     *
     * Not the dealer's standing number, which is the obvious knob and the wrong
     * one: 17 is close to optimal, so moving it EITHER way weakens the dealer.
     * Standing on 16 leaves them beatable by a 17, and hitting to 18 makes them
     * bust far more often. A difficulty where hard is easier is worse than no
     * difficulty at all.
     *
     * The rule that genuinely favours the house is hitting SOFT 17, a 17 with
     * an ace still counted as eleven, where another card cannot bust them and
     * can only improve the hand. That is the standard house-edge rule and it is
     * what hard mode uses.
     *
     * The bank is the other half, and the blunter one: the score is your PEAK,
     * so a short bank can end a run before a good streak ever arrives. The bet
     * stays at 10 everywhere, so a chip means the same thing at every level.
     */
    const HITS_SOFT_17 = ctx.level === 'difficile';
    const START_CHIPS = byLevel(ctx.level, 150, 100, 70);

    let chips = START_CHIPS;
    let peak = START_CHIPS;
    let inHand = false;

    const refillShoe = (): void => {
      shoe = shuffle(RANKS.flatMap((r) => SUITS.map((s) => `${r}${s}`)));
    };

    const draw = (): string => {
      if (shoe.length < 8) refillShoe();
      return shoe.pop()!;
    };

    /**
     * Hand value with aces counted as 11 where that does not bust.
     *
     * Written as "count them all as 11, then demote while over 21", which is the
     * only formulation that gets A A 9 right (21, not 31 and not 11).
     */
    const valueOf = (hand: string[]): number => {
      let total = 0;
      let aces = 0;
      for (const card of hand) {
        const rank = card.slice(0, -1);
        if (rank === 'A') {
          aces++;
          total += 11;
        } else if (['J', 'Q', 'K'].includes(rank)) total += 10;
        else total += Number(rank);
      }
      while (total > 21 && aces > 0) {
        total -= 10;
        aces--;
      }
      return total;
    };

    /**
     * A 17 that still has an ace counted as eleven.
     *
     * Worth its own function because it is the whole of the hard rule: another
     * card cannot bust this hand, only demote the ace and improve it, which is
     * exactly why drawing to it favours the dealer.
     */
    const isSoft17 = (hand: string[]): boolean => {
      if (valueOf(hand) !== 17) return false;
      let hard = 0;
      for (const card of hand) {
        const rank = card.slice(0, -1);
        if (rank === 'A') hard += 1;
        else if (['J', 'Q', 'K'].includes(rank)) hard += 10;
        else hard += Number(rank);
      }
      // Counting every ace as one gives a different total only when one of them
      // is currently worth eleven.
      return hard !== 17;
    };

    const cardsRow = (hand: string[], hideSecond: boolean): HTMLElement => {
      const row = h('div');
      row.style.cssText = 'display:flex;gap:8px;justify-content:center;flex-wrap:wrap';
      hand.forEach((card, i) => {
        const hidden = hideSecond && i === 1;
        const el = h('div', '', hidden ? '?' : card);
        const red = !hidden && (card.includes('♥') || card.includes('♦'));
        el.style.cssText =
          'width:56px;height:78px;border-radius:9px;display:grid;place-items:center;' +
          `font-size:19px;font-weight:800;border:1px solid #2a1c47;background:${hidden ? '#241539' : '#f4f0ff'};` +
          `color:${hidden ? '#a394c8' : red ? '#dc2626' : '#100a1e'}`;
        row.append(el);
      });
      return row;
    };

    const table = h('div');
    table.style.cssText = 'display:flex;flex-direction:column;gap:14px;align-items:center;width:100%';

    const paint = (hideDealer: boolean): void => {
      table.replaceChildren();
      table.append(h('p', 'ac-note', 'Croupier'), cardsRow(theirs, hideDealer));
      table.append(h('p', 'ac-note', 'Toi'), cardsRow(mine, false));
      youValue.textContent = String(valueOf(mine));
      dealerValue.textContent = hideDealer ? '?' : String(valueOf(theirs));
      chipValue.textContent = String(chips);
    };

    const settle = (outcome: 'win' | 'lose' | 'push', why: string): void => {
      inHand = false;
      if (outcome === 'win') {
        chips += 10;
        peak = Math.max(peak, chips);
        ctx.submit(peak);
        tone(760, 120, 'triangle');
      } else if (outcome === 'lose') {
        chips -= 10;
        tone(170, 200, 'square');
      }
      paint(false);
      note.textContent = `${why} Jetons: ${chips}.`;
      hit.disabled = true;
      stand.disabled = true;
      deal.hidden = false;
      deal.textContent = chips >= 10 ? 'Main suivante' : 'Recommencer a 100';
    };

    const dealerPlay = (): void => {
      // Draw to 16, stand on all 17, exactly as published. Hard mode adds the
      // one house rule on top: a soft 17 is drawn to as well.
      while (valueOf(theirs) < 17 || (HITS_SOFT_17 && isSoft17(theirs))) theirs.push(draw());
      const me = valueOf(mine);
      const them = valueOf(theirs);
      if (them > 21) settle('win', 'Le croupier depasse.');
      else if (them > me) settle('lose', `Le croupier fait ${them}.`);
      else if (them < me) settle('win', `Tu fais ${me} contre ${them}.`);
      else settle('push', 'Égalité.');
    };

    const newHand = (): void => {
      if (chips < 10) {
        chips = 100;
        peak = Math.max(peak, 100);
      }
      mine = [draw(), draw()];
      theirs = [draw(), draw()];
      inHand = true;
      hit.disabled = false;
      stand.disabled = false;
      deal.hidden = true;
      note.textContent = 'Tirer ou rester.';
      paint(true);
      if (valueOf(mine) === 21) {
        // A natural pays immediately rather than making the player press stand.
        settle('win', 'Blackjack.');
      }
    };

    const hit = h('button', 'ac-primary', 'Tirer') as HTMLButtonElement;
    hit.addEventListener('click', () => {
      if (!inHand) return;
      mine.push(draw());
      tone(480, 45);
      paint(true);
      if (valueOf(mine) > 21) settle('lose', `Tu depasses avec ${valueOf(mine)}.`);
    });

    const stand = h('button', 'ac-ghost', 'Rester') as HTMLButtonElement;
    stand.style.cssText = 'padding:12px 22px;font-size:15px';
    stand.addEventListener('click', () => {
      if (!inHand) return;
      inHand = false;
      dealerPlay();
    });

    const deal = h('button', 'ac-primary', 'Distribuer');
    deal.addEventListener('click', newHand);

    const controls = h('div');
    controls.style.cssText = 'display:flex;gap:10px;flex-wrap:wrap;justify-content:center';
    controls.append(hit, stand, deal);

    const note = h('p', 'ac-note', 'Cent jetons, dix par main.');

    refillShoe();
    hit.disabled = true;
    stand.disabled = true;
    root.append(hud, table, controls, note);
    newHand();
    return () => life.stop();
  },
};
